// src/view/RegistrarSegPracticaAprendiz.jsx
import axios from "axios";
import Swal from "sweetalert2";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

/* =================== Permisos / Auth =================== */
const MODULO = "SeguimientoPractica";

const normalizarTexto = (texto = "") =>
  texto
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const leerJSONLocalStorage = (key, fallback = null) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    console.error(`Error leyendo ${key} desde localStorage:`, error);
    return fallback;
  }
};

const obtenerUsuarioStorage = () =>
  leerJSONLocalStorage("usuario", null) ||
  leerJSONLocalStorage("user", null) ||
  leerJSONLocalStorage("usuarios", null) ||
  {};

const obtenerModulosPermisos = () => {
  const permisosDirectos = leerJSONLocalStorage("permisos", []);
  const usuario = obtenerUsuarioStorage();

  if (Array.isArray(permisosDirectos) && permisosDirectos.length) {
    return permisosDirectos;
  }

  return (
    usuario?.modulos ||
    usuario?.permisos ||
    usuario?.rol?.modulos ||
    usuario?.rol?.permisos ||
    usuario?.usuario?.modulos ||
    usuario?.usuario?.permisos ||
    usuario?.user?.modulos ||
    usuario?.user?.permisos ||
    []
  );
};

const esSuperAdmin = () => {
  const usuario = obtenerUsuarioStorage();

  const rol =
    usuario?.rol?.nombreRol ||
    usuario?.rol?.nombre ||
    usuario?.rol ||
    usuario?.role ||
    usuario?.tipoUsuario ||
    usuario?.nombreRol ||
    usuario?.usuario?.rol ||
    usuario?.user?.rol ||
    "";

  const rolNormalizado = normalizarTexto(rol);

  return (
    rolNormalizado.includes("super_admin") ||
    rolNormalizado.includes("superadmin") ||
    rolNormalizado.includes("administrador")
  );
};

const obtenerPermisosModulo = (modulo) => {
  if (esSuperAdmin()) {
    return {
      modulo,
      LEER: true,
      CREAR: true,
      ACTUALIZAR: true,
      ELIMINAR: true,
      leer: true,
      crear: true,
      actualizar: true,
      eliminar: true,
    };
  }

  const modulos = obtenerModulosPermisos();

  return (
    modulos.find((permiso) => {
      const nombreModulo =
        permiso?.modulo ||
        permiso?.nombreModulo ||
        permiso?.moduloNombre ||
        permiso?.nombre ||
        permiso?.name ||
        "";

      return normalizarTexto(nombreModulo) === normalizarTexto(modulo);
    }) || {}
  );
};

const tienePermiso = (permisoModulo, accion) => {
  if (esSuperAdmin()) return true;
  if (!permisoModulo) return false;

  const accionMayuscula = accion.toUpperCase();
  const accionMinuscula = accion.toLowerCase();
  const accionNormalizada = normalizarTexto(accion);

  const alias = {
    leer: ["LEER", "leer", "VER", "ver", "READ", "read", "LISTAR", "listar"],
    crear: ["CREAR", "crear", "CREATE", "create", "ADD", "add"],
    actualizar: [
      "ACTUALIZAR",
      "actualizar",
      "EDITAR",
      "editar",
      "UPDATE",
      "update",
      "EDIT",
      "edit",
    ],
    eliminar: ["ELIMINAR", "eliminar", "DELETE", "delete", "BORRAR", "borrar"],
  };

  const accionesValidas = alias[accionNormalizada] || [accionMayuscula, accionMinuscula];

  const campoDirecto = accionesValidas.some((campo) => permisoModulo?.[campo] === true);

  const campoPuede = accionesValidas.some((campo) => {
    const campoNormalizado = normalizarTexto(campo);
    const nombreCampo = `puede${campoNormalizado.charAt(0).toUpperCase()}${campoNormalizado.slice(1)}`;
    return permisoModulo?.[nombreCampo] === true;
  });

  const enArreglos = [
    ...(Array.isArray(permisoModulo?.acciones) ? permisoModulo.acciones : []),
    ...(Array.isArray(permisoModulo?.permisos) ? permisoModulo.permisos : []),
    ...(Array.isArray(permisoModulo?.operaciones) ? permisoModulo.operaciones : []),
  ].some((item) => accionesValidas.map(normalizarTexto).includes(normalizarTexto(item)));

  return campoDirecto || campoPuede || enArreglos;
};

const authConfig = () => {
  const token = localStorage.getItem("token");

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

/* =================== Helpers de UI =================== */
const EmptyState = ({
  title = "Sin registros",
  subtitle = "Aún no has agregado información.",
}) => (
  <div className="text-center text-muted py-4">
    <div className="display-6 mb-2">🗂️</div>
    <div className="fw-semibold">{title}</div>
    <div className="small">{subtitle}</div>
  </div>
);

const Pill = ({ children, variant = "secondary" }) => (
  <span className={`badge rounded-pill text-bg-${variant}`}>{children}</span>
);

const IconBubble = ({ icon = "fa-regular fa-note-sticky", tone = "primary" }) => {
  const toneMap = {
    primary: "bg-primary-subtle text-primary",
    success: "bg-success-subtle text-success",
    info: "bg-info-subtle text-info",
    warning: "bg-warning-subtle text-warning",
    danger: "bg-danger-subtle text-danger",
    secondary: "bg-secondary-subtle text-secondary",
    dark: "bg-dark-subtle text-dark",
  };
  return (
    <div
      className={`rounded-circle d-flex align-items-center justify-content-center ${toneMap[tone] || toneMap.primary}`}
      style={{ width: 44, height: 44, minWidth: 44 }}
    >
      <i className={icon}></i>
    </div>
  );
};

const SectionCard = ({ icon, tone = "primary", title, subtitle, children, action }) => (
  <div className="card border-0 shadow-sm h-100 section-card">
    <div className="card-body py-3">
      <div className="d-flex align-items-start gap-3">
        <IconBubble icon={icon} tone={tone} />
        <div className="w-100">
          <div className="d-flex justify-content-between align-items-center">
            <div className="fw-semibold">{title}</div>
            {action}
          </div>
          {subtitle && <div className="small text-muted mt-1">{subtitle}</div>}
          {children}
        </div>
      </div>
    </div>
  </div>
);

/* =================== ENUMS del Schema =================== */
const ENUMS = {
  modalidadSesion: ["Presencial", "Virtual"],
  modalidadSeguimiento: ["Presencial", "Virtual", "A Distancia", "Híbrida"],
  formaEvaluacion: ["Virtual", "Presencial"],
  valoracion: ["Satisfactorio", "Por mejorar"],
  factoresTecnicos: [
    "Aplicación de conocimiento",
    "Mejora continua",
    "Fortalecimiento ocupacional",
    "Oportunidad y calidad",
    "Responsabilidad ambiental",
    "Administración de recursos",
    "Seguridad y salud en el trabajo",
    "Documentación etapa productiva",
  ],
  factoresActitudinales: [
    "Relaciones interpersonales",
    "Trabajo en equipo",
    "Solución de problemas",
    "Cumplimiento",
    "Organización",
  ],
  juicioEvaluacion: ["Aprobado", "No aprobado"],
};

/* =================== Modales =================== */

// Modal genérico para arreglos de texto (M1) con EDITAR
const ArrayEditorModal = ({
  show,
  onClose,
  title,
  placeholder,
  items = [],
  onAdd,
  onRemove,
  onEdit,
}) => {
  const [value, setValue] = useState("");
  const [editIdx, setEditIdx] = useState(-1);
  const [editValue, setEditValue] = useState("");

  if (!show) return null;

  const handleAdd = () => {
    const v = (value || "").trim();
    if (!v) return;
    onAdd?.(v);
    setValue("");
  };

  const startEdit = (idx, current) => {
    setEditIdx(idx);
    setEditValue(current);
  };

  const cancelEdit = () => {
    setEditIdx(-1);
    setEditValue("");
  };

  const saveEdit = async (idx) => {
    const newVal = (editValue || "").trim();
    if (!newVal) return;
    const oldVal = items[idx];
    if (oldVal === newVal) {
      cancelEdit();
      return;
    }
    await onEdit?.(oldVal, newVal);
    cancelEdit();
  };

  return (
    <>
      <div className="modal show" style={{ display: "block" }} role="dialog" aria-modal="true">
        <div className="modal-dialog modal-lg modal-dialog-centered" style={{ maxWidth: "50vw" }}>
          <div className="modal-content shadow border-0 rounded-4">
            <style>{`
              .aem-body { max-height: 65vh; overflow: auto; }
              .aem-row { display: grid; grid-template-columns: 1fr auto; align-items: start; gap: .75rem; }
              .aem-text { min-width: 0; word-break: break-word; white-space: normal; }
              .aem-actions { display: flex; gap: .5rem; align-items: center; flex-wrap: nowrap; }
              .aem-footer { position: sticky; bottom: 0; background: #fff; box-shadow: 0 -4px 16px rgba(16,24,40,.06); z-index: 1; }
            `}</style>

            <div className="modal-header border-0 pb-0">
              <div>
                <h5 className="modal-title fw-bold">{title}</h5>
                <div className="small text-muted">
                  <i className="fa-regular fa-list-check me-1"></i>
                  Edición directa (push/pull contra el API).
                </div>
              </div>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>

            <div className="modal-body aem-body">
              {items.length === 0 ? (
                <EmptyState subtitle="Agrega el primer elemento con el campo inferior." />
              ) : (
                <div className="list-group rounded-3 border mb-3">
                  {items.map((t, i) => (
                    <div key={`it-${i}`} className="list-group-item">
                      <div className="aem-row">
                        <div className="aem-text">
                          {editIdx === i ? (
                            <textarea
                              className="form-control form-control-sm"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              rows={2}
                              onKeyDown={(e) => e.key === "Enter" && e.ctrlKey && saveEdit(i)}
                              autoFocus
                            />
                          ) : (
                            <div style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
                              {t}
                            </div>
                          )}
                        </div>
                        <div className="aem-actions">
                          {editIdx === i ? (
                            <>
                              <button className="btn btn-sm btn-success" type="button" title="Guardar" onClick={() => saveEdit(i)}>
                                <i className="fa-solid fa-check" />
                              </button>
                              <button className="btn btn-sm btn-outline-secondary" type="button" title="Cancelar" onClick={cancelEdit}>
                                <i className="fa-solid fa-rotate-left" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button className="btn btn-sm btn-outline-primary" type="button" title="Editar" onClick={() => startEdit(i, t)}>
                                <i className="fa-solid fa-pen" />
                              </button>
                              <button className="btn btn-sm btn-outline-danger" type="button" onClick={() => onRemove?.(t)} title="Eliminar">
                                <i className="fa-solid fa-trash" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-3">
                <label className="form-label small fw-semibold">Nuevo elemento</label>
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <i className="fa-regular fa-square-plus" />
                  </span>
                  <input
                    className="form-control"
                    placeholder={placeholder || "Texto…"}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                  />
                  <button className="btn btn-success" type="button" onClick={handleAdd}>
                    <i className="fa-solid fa-plus me-1" /> Añadir
                  </button>
                </div>
                <div className="form-text">
                  Se envía un <code>push</code> / <code>pull</code> al backend.
                </div>
              </div>
            </div>

            <div className="modal-footer border-0 aem-footer d-flex justify-content-end gap-2">
              <button className="btn btn-secondary" onClick={onClose}>Cerrar</button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  );
};

// Tabla modal factores – muestra valores actuales
const FactoresTableModal = ({
  show,
  onClose,
  title,
  variables = [],
  valoracionEnum = [],
  values = {}, // { variable: { valoracion, observaciones } }
  onSet,
}) => {
  if (!show) return null;

  const handleChange = (variable, field, value) => {
    onSet?.(variable, field, value);
  };

  return (
    <>
      <div className="modal show" style={{ display: "block" }} role="dialog" aria-modal="true">
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content shadow-lg border-0 rounded-4">
            <div className="modal-header border-0 pb-0">
              <div>
                <h5 className="modal-title mb-1">{title}</h5>
                <div className="small text-muted">
                  <i className="fa-regular fa-rectangle-list me-1"></i>
                  {variables.length} variables
                </div>
              </div>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>

            <div className="modal-body pt-3">
              {variables.length === 0 ? (
                <EmptyState subtitle="No hay variables definidas." />
              ) : (
                <div className="table-responsive rounded-3 border">
                  <table className="table table-sm table-striped align-middle mb-0">
                    <thead className="table-light sticky-top">
                      <tr className="text-muted">
                        <th>Variable</th>
                        <th style={{ width: 220 }}>Valoración</th>
                        <th>Observaciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {variables.map((v) => (
                        <tr key={v}>
                          <td className="fw-semibold">{v}</td>
                          <td>
                            <select
                              className="form-select form-select-sm"
                              defaultValue={values?.[v]?.valoracion || ""}
                              onChange={(e) => handleChange(v, "valoracion", e.target.value)}
                            >
                              <option value="" disabled>Seleccione…</option>
                              {valoracionEnum.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <div className="input-group input-group-sm">
                              <span className="input-group-text bg-white">
                                <i className="fa-regular fa-comment-dots"></i>
                              </span>
                              <input
                                className="form-control"
                                placeholder="Observaciones"
                                defaultValue={values?.[v]?.observaciones || ""}
                                onBlur={(e) => handleChange(v, "observaciones", e.target.value)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="table-light">
                      <tr>
                        <td colSpan={3} className="small text-muted py-2 px-3">
                          <i className="fa-regular fa-circle-question me-1"></i>
                          Envía <code>{`[{ variable, valoracion, observaciones }]`}</code> al backend.
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            <div className="modal-footer border-0">
              <button className="btn btn-secondary" onClick={onClose}>Cerrar</button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show"></div>
    </>
  );
};

/* =================== Utils de datos =================== */
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const safe = (v, fallback = "—") => (v ?? v === 0 ? v : fallback);
const fullName = (p1, p2, a1, a2) => [p1, p2, a1, a2].filter(Boolean).join(" ").trim() || "—";

// Normaliza una lista a strings (toma key si vienen objetos)
const toStringList = (list, key) => {
  if (!Array.isArray(list)) return [];
  return list
    .map((it) => {
      if (typeof it === "string") return it.trim();
      if (typeof it === "number") return String(it);
      if (it && typeof it === "object") {
        const val = it[key] ?? it.texto ?? it.nombre ?? it.descripcion ?? "";
        return String(val).trim();
      }
      return "";
    })
    .filter(Boolean);
};

// convierte arrays [{variable,valoracion,observaciones}] a mapa { variable: {valoracion,observaciones} }
const mapByVariable = (arr = []) =>
  arr?.reduce((acc, it) => {
    if (it?.variable) {
      acc[it.variable] = {
        valoracion: it.valoracion || "",
        observaciones: it.observaciones || "",
      };
    }
    return acc;
  }, {}) || {};

/* =================== Página: RegistrarSegPracticaAprendiz =================== */
const RegistrarSegPracticaAprendiz = () => {
  const permisosModulo = obtenerPermisosModulo(MODULO);

  const puedeLeer = tienePermiso(permisosModulo, "leer");
  const puedeCrear = tienePermiso(permisosModulo, "crear");
  const puedeActualizar = tienePermiso(permisosModulo, "actualizar");
  const puedeEliminar = tienePermiso(permisosModulo, "eliminar");

  // Estado UI para modales
  const [showFTm2, setShowFTm2] = useState(false);
  const [showFAm2, setShowFAm2] = useState(false);
  const [showFTm3, setShowFTm3] = useState(false);
  const [showFAm3, setShowFAm3] = useState(false);

  const [showComp, setShowComp] = useState(false);
  const [showRes, setShowRes] = useState(false);
  const [showActs, setShowActs] = useState(false);
  const [showEvi, setShowEvi] = useState(false);
  const [showObs, setShowObs] = useState(false);

  // IDs de la ruta
  const { etapaId, matriculaId, aprendizId } = useParams();
  const location = useLocation();


  // Estado de red/meta/formato
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [meta, setMeta] = useState({
    etapa: null,
    aprendiz: null,
    matricula: null,
  });

  // El/los formatos existentes para esa relación (normalmente 1)
  const [formatos, setFormatos] = useState([]);
  const formato = formatos?.[0] || null;
  const form = formato || {}; // atajo

  // ref del formulario para leer valores al enviar
  const formRef = useRef(null);

  // Campos simples que se envían en el patch
  const FIELD_NAMES = useMemo(() => ([
    // M1
    "m1_ciudad",
    "m1_fechaDiligenciamiento",
    "m1_modalidadSesion",
    // M2
    "m2_fechaMomentoSeguimiento",
    "m2_modalidadSeguimiento",
    "m2_enlaceGrabacion",
    "m2_obs_ComplementInst",
    "m2_obs_Aprendiz",
    "m2_obs_EnteCoformador",
    "m2_ciudad",
    "m2_fechaDiligenciamiento",
    "m2_modalidadSesion",
    // M3
    "m3_numeroVisitasRealizadas",
    "m3_formaEvaluacion",
    "m3_enlaceGrabacion",
    "m3_retroEnte_proceso",
    "m3_retroInstructor_proceso",
    "m3_retroAprendiz_proceso",
    "m3_retroEnte_desempeno",
    "m3_retroInstructor_desempeno",
    "m3_retroAprendiz_desempeno",
    "m3_juicioEvaluacion",
    "m3_modalidadSesion",
    "m3_ciudad",
    "m3_fechaDiligenciamiento",
  ]), []);

  /* ========== Llamadas axios explícitas (sin objeto api) ========== */

  // GET /api/seguimiento/listbyrelacion/:etapaId/:aprendizId/:matriculaId
  const fetchByRelacion = async (etapa, aprendiz, matricula) => {
    const url = `/api/seguimiento/listbyrelacion/${etapa}/${aprendiz}/${matricula}`;
    console.log(`[GET] ${url} → listByRelacion`);
    return axios.get(url, authConfig());
  };

  // POST /api/seguimiento/add
  const createSeguimiento = async (payload) => {
    const url = `/api/seguimiento/add`;
    console.log(`[POST] ${url} → add`, payload);
    return axios.post(url, payload, authConfig());
  };

  // PUT /api/seguimiento/update/:id
  const putSeguimiento = async (id, body) => {
    const url = `/api/seguimiento/update/${id}`;
    console.log(`[PUT] ${url} → update`, body);
    return axios.put(url, body, authConfig());
  };

  // DELETE /api/seguimiento/delete/:id
  const deleteSeguimiento = async (id) => {
    const url = `/api/seguimiento/delete/${id}`;
    console.log(`[DELETE] ${url} → delete`);
    return axios.delete(url, authConfig());
  };

  // Helpers push/pull/replace para arrays M1 (PUT directo)
  const pushToArray = async (id, path, values = []) => {
    if (!id || !path || !values.length) return;
    await putSeguimiento(id, { push: { [path]: values } });
  };
  const pullFromArray = async (id, path, values = []) => {
    if (!id || !path || !values.length) return;
    await putSeguimiento(id, { pull: { [path]: values } });
  };
  const replaceInArray = async (id, path, oldVal, newVal) => {
    if (!id || !path || !oldVal || !newVal) return;
    await putSeguimiento(id, { pull: { [path]: [oldVal] }, push: { [path]: [newVal] } });
  };

  // Carga meta + formatos por relación
  const loadMeta = useCallback(async () => {
    if (!puedeLeer) {
      setLoading(false);
      Swal.fire({
        icon: "warning",
        title: "Sin permiso",
        text: "No tienes permiso para leer seguimiento de práctica.",
      });
      return;
    }

    if (!etapaId || !aprendizId || !matriculaId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await fetchByRelacion(etapaId, aprendizId, matriculaId);
      setMeta({
        etapa: data?.etapa || null,
        aprendiz: data?.aprendiz || null,
        matricula: data?.matricula || null,
      });
      setFormatos(Array.isArray(data?.seguimiento) ? data.seguimiento : []);
    } catch (err) {
      console.error("Error listByRelacion:", err);
      setMeta({ etapa: null, aprendiz: null, matricula: null });
      setFormatos([]);
    } finally {
      setLoading(false);
    }
  }, [etapaId, aprendizId, matriculaId, puedeLeer]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  // Datos derivados (tarjetas)
  const nombreAprendiz = () => {
    const a = meta?.aprendiz;
    if (!a) return "—";
    return fullName(
      a?.nombre1Aprendiz,
      a?.nombre2Aprendiz,
      a?.apellido1Aprendiz,
      a?.apellido2Aprendiz
    );
  };

  const grupo = meta?.matricula?.grupoObjectId;
  const programa = grupo?.programaObjectId;

  const nombreEnte =
    meta?.etapa?.nombreEnteconformador ??
    meta?.matricula?.enteObjectId?.nombreEnteconformador ??
    "—";

  const instructor = meta?.etapa?.instructorObjectId || null;
  const jefe = meta?.etapa?.jefeObjectId || null;

  const instructorNombre = instructor
    ? fullName(
      instructor?.nombre1Instructor,
      instructor?.nombre2Instructor,
      instructor?.apellido1Instructor,
      instructor?.apellido2Instructor
    )
    : "—";

  const jefeNombre = jefe
    ? fullName(
      jefe?.nombre1Jefe,
      jefe?.nombre2Jefe,
      jefe?.apellido1Jefe,
      jefe?.apellido2Jefe
    )
    : "—";

  const etapaFechaInicio =
    meta?.etapa?.fechaInicioEtapaPractica || meta?.etapa?.fechaInicio;
  const etapaFechaFin =
    meta?.etapa?.fechaFinEtapaPractica || meta?.etapa?.fechaFin;

  // ====== Items (M1) ======
  const pathPT = {
    comp: "m1_competenciasADesarrollar",
    res: "m1_resultadosAprendizaje",
    acts: "m1_actividadesADesarrollar",
    evi: "m1_evidenciasAprendizaje",
    obs: "m1_observacionesAdicionales",
  };

  const itemsPT = {
    comp: toStringList(form?.m1_competenciasADesarrollar, "texto"),
    res: toStringList(form?.m1_resultadosAprendizaje, "texto"),
    acts: toStringList(form?.m1_actividadesADesarrollar, "texto"),
    evi: toStringList(form?.m1_evidenciasAprendizaje, "texto"),
    obs: toStringList(form?.m1_observacionesAdicionales, "texto"),
  };

  // ===== Mapas para valoraciones (M2/M3) =====
  const m2TecValues = useMemo(() => mapByVariable(form?.m2_factoresTecnicos), [form?.m2_factoresTecnicos]);
  const m2ActValues = useMemo(() => mapByVariable(form?.m2_factoresActitudinales), [form?.m2_factoresActitudinales]);
  const m3TecValues = useMemo(() => mapByVariable(form?.m3_factoresTecnicos), [form?.m3_factoresTecnicos]);
  const m3ActValues = useMemo(() => mapByVariable(form?.m3_factoresActitudinales), [form?.m3_factoresActitudinales]);

  // ===== Título dinámico =====
  const qs = new URLSearchParams(location.search);
  const tituloOverride = (qs.get("titulo") || "").trim();
  const tituloPagina = tituloOverride.length
    ? tituloOverride
    : "Seguimiento Etapa Práctica";

  /* ========== Guardar (crear/actualizar) ========== */
  const saveSeguimiento = async () => {
    if (!puedeCrear) {
      Swal.fire({
        icon: "warning",
        title: "Sin permiso",
        text: "No tienes permiso para crear seguimiento de práctica.",
      });
      return;
    }
    setSaving(true);
    try {
      if (!etapaId || !aprendizId || !matriculaId) {
        Swal.fire({
          icon: "warning",
          title: "Faltan identificadores",
          text: "Verifica etapaId, aprendizId y matriculaId.",
        });
        return;
      }
      const payload = {
        etapaPracticaObjectId: etapaId,
        aprendizObjectId: aprendizId,
        matriculaObjectId: matriculaId,
      };
      await createSeguimiento(payload); // POST /api/seguimiento/add
      await loadMeta();
      Swal.fire({
        icon: "success",
        title: "Seguimiento creado con éxito",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error al guardar",
        text: err?.response?.data?.message || "No se pudo crear el seguimiento.",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateSeguimiento = async (patch = {}) => {
    if (!puedeActualizar) {
      Swal.fire({
        icon: "warning",
        title: "Sin permiso",
        text: "No tienes permiso para actualizar seguimiento de práctica.",
      });
      return;
    }
    setSaving(true);
    try {
      const segId = formato?._id;
      if (!segId) {
        Swal.fire({
          icon: "warning",
          title: "No existe en BD",
          text: "Aún no hay _id de seguimiento para actualizar. Presiona Guardar primero.",
        });
        return;
      }
      await putSeguimiento(segId, Object.keys(patch).length ? patch : { _touch: true }); // PUT /api/seguimiento/update/:id
      await loadMeta();
      Swal.fire({
        icon: "success",
        title: "Seguimiento actualizado con éxito",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error al actualizar",
        text: err?.response?.data?.message || "No se pudo actualizar el seguimiento.",
      });
    } finally {
      setSaving(false);
    }
  };

  // onSubmit
  const actions = async (e) => {
    e.preventDefault();
    if (!formato?._id) {
      if (!puedeCrear) {
        Swal.fire({
          icon: "warning",
          title: "Sin permiso",
          text: "No tienes permiso para crear seguimiento de práctica.",
        });
        return;
      }
      await saveSeguimiento();
    } else if (!puedeActualizar) {
      Swal.fire({
        icon: "warning",
        title: "Sin permiso",
        text: "No tienes permiso para actualizar seguimiento de práctica.",
      });
      return;
    }

    // Armar patch desde el formulario (solo campos que cambian)
    const fd = new FormData(formRef.current);
    const patch = {};
    const castMaybeNumber = (name, val) =>
      name === "m3_numeroVisitasRealizadas" ? (val === "" ? "" : Number(val)) : val;

    FIELD_NAMES.forEach((name) => {
      const newVal = castMaybeNumber(name, fd.get(name) ?? "");
      const oldVal = form?.[name] ?? "";
      if (String(newVal) !== String(oldVal)) {
        patch[name] = newVal;
      }
    });

    await updateSeguimiento(patch);
  };

  // Eliminar
  const handleEliminar = async () => {
    if (!puedeEliminar) {
      Swal.fire({
        icon: "warning",
        title: "Sin permiso",
        text: "No tienes permiso para eliminar seguimiento de práctica.",
      });
      return;
    }

    if (!formato?._id) return;
    if (!window.confirm("¿Eliminar el formato SENA-032? Esta acción no se puede deshacer.")) return;
    try {
      await deleteSeguimiento(formato._id); // DELETE /api/seguimiento/delete/:id
      await loadMeta();
      Swal.fire({
        icon: "success",
        title: "Formato eliminado",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error eliminando",
        text: err?.response?.data?.message || "Error eliminando.",
      });
    }
  };

  if (!puedeLeer) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning shadow-sm rounded-4">
          <h5 className="fw-bold mb-1">Sin permiso</h5>
          <div>No tienes permiso para consultar el módulo Seguimiento de Práctica.</div>
        </div>
      </div>
    );
  }

  /* =================== Estilos locales + Render =================== */
  return (
    <form className="container-fluid py-4 page-bg" onSubmit={actions} ref={formRef}>
      <style>{`
        .page-bg { background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%); min-height: 100%; }
        .section-card { border-radius: 1rem; box-shadow: 0 8px 24px rgba(16,24,40,.06); }
        .soft-separator { height: 1px; background: linear-gradient(90deg, transparent, rgba(0,0,0,.08), transparent); }
        .tabs-wrap .nav-link { border: none; border-bottom: 2px solid transparent; color: #64748b; font-weight: 600; padding: .75rem 1rem; border-radius: .5rem .5rem 0 0; }
        .tabs-wrap .nav-link:hover { color: #0ea5e9; }
        .tabs-wrap .nav-link.active { color: #0f172a; border-color: #0ea5e9; background: #ffffff; }
        .btn-ghost { background: #fff; border: 1px solid rgba(0,0,0,.08); }
      `}</style>

      {/* Encabezado + acciones */}
      <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-3">
        <div>
          <h1 className="fw-bold mb-1">
            <span className="me-2">
              <i className="fa-solid fa-file-pen text-primary"></i>
            </span>
            {tituloPagina}
          </h1>
        </div>

        <div className="d-flex flex-wrap align-items-center gap-2">
          {formato && puedeEliminar && (
            <button className="btn btn-outline-danger" type="button" onClick={handleEliminar}>
              <i className="fa-regular fa-trash-can me-1"></i> Eliminar
            </button>
          )}
          <button className="btn btn-ghost" type="button" disabled={saving}>
            <i className="fa-regular fa-circle-xmark me-1"></i> Cancelar
          </button>
          {((!formato && puedeCrear) || (formato && puedeActualizar)) && (
            <button className="btn btn-primary" type="submit" disabled={saving || loading}>
              <i className="fa-solid fa-floppy-disk me-1"></i> {formato ? "Actualizar" : "Guardar"}
            </button>
          )}
        </div>
      </div>

      {/* Tarjetas superiores */}
      {!loading ? (
          <div className="row g-3 mb-4">
            {/* Aprendiz */}
            <div className="col-12 col-lg-6">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body py-3">
                  <div className="d-flex align-items-start gap-3">
                    <div className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center" style={{ width: 42, height: 42 }}>
                      <i className="fa-solid fa-user-graduate"></i>
                    </div>
                    <div className="w-100">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="fw-semibold">{nombreAprendiz()}</div>
                      </div>
                      <div className="small text-muted mt-1">
                        Documento: {safe(meta?.aprendiz?.documentoAprendiz)} · Tel: {safe(meta?.aprendiz?.telefono1Aprendiz)} · Email: {safe(meta?.aprendiz?.emailAprendiz)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Etapa */}
            <div className="col-12 col-lg-6">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body py-3">
                  <div className="d-flex align-items-start gap-3">
                    <div className="rounded-circle bg-success-subtle text-success d-flex align-items-center justify-content-center" style={{ width: 42, height: 42 }}>
                      <i className="fa-regular fa-calendar-check"></i>
                    </div>
                    <div>
                      <div className="small text-muted">Fecha de la Etapa Productiva</div>
                      <div className="fw-semibold">
                        {fmtDate(etapaFechaInicio)} — {fmtDate(etapaFechaFin)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Programa */}
            <div className="col-12 col-md-6 col-lg-4">
              <SectionCard icon="fa-solid fa-book" tone="info" title="Programa de Formación" subtitle={safe(programa?.nombrePrograma)} />
            </div>

            {/* Grupo */}
            <div className="col-12 col-md-6 col-lg-4">
              <SectionCard icon="fa-solid fa-people-group" tone="warning" title="Grupo" subtitle={safe(grupo?.codigoGrupo, meta?.matricula?.fichaGrupo ?? "—")} />
            </div>

            {/* Ente */}
            <div className="col-12 col-md-6 col-lg-4">
              <SectionCard icon="fa-solid fa-building" tone="secondary" title="Ente Conformador" subtitle={safe(nombreEnte)} />
            </div>

            {/* Instructor */}
            <div className="col-12 col-md-6">
              <SectionCard icon="fa-solid fa-chalkboard-user" tone="primary" title="Instructor de Seguimiento" subtitle={safe(instructorNombre)}>
                <div className="small text-muted mt-2">
                  Doc: {safe(instructor?.documentoInstructor)} · Tel: {safe(instructor?.telefono1Instructor ?? instructor?.telefonoInstructor)} · Email: {safe(instructor?.emailInstructor)}
                </div>
              </SectionCard>
            </div>

            {/* Jefe */}
            <div className="col-12 col-md-6">
              <SectionCard icon="fa-solid fa-user-tie" tone="dark" title="Jefe Inmediato" subtitle={safe(jefeNombre)}>
                <div className="small text-muted mt-2">
                  Doc: {safe(jefe?.documentoJefe)} · Tel: {safe(jefe?.telefono1Jefe ?? meta?.etapa?.enteObjectId?.jefeTelefono)} · Email: {safe(jefe?.emailJefe ?? meta?.etapa?.enteObjectId?.jefeCorreo)}
                </div>
              </SectionCard>
            </div>
          </div>
        ) : (
          <div className="row g-3 mb-4">
            <div className="col-12 col-lg-6">
              <SectionCard icon="fa-solid fa-user-graduate" tone="primary" title="Cargando aprendiz…" subtitle="—" />
            </div>
            <div className="col-12 col-lg-6">
              <SectionCard icon="fa-regular fa-calendar-check" tone="success" title="Cargando etapa…" subtitle="—" />
            </div>
          </div>
        )
      }

      {/* Tabs */}
      <div className="card shadow-sm border-0 rounded-4">
        <div className="card-body p-0">
          <div className="tabs-wrap px-3 pt-3">
            <ul className="nav nav-tabs" role="tablist">
              <li className="nav-item">
                <button className="nav-link active" data-bs-toggle="tab" data-bs-target="#m1" type="button">
                  <i className="fa-regular fa-compass me-2"></i>
                  Momento 1 · Planeación
                </button>
              </li>
              <li className="nav-item">
                <button className="nav-link" data-bs-toggle="tab" data-bs-target="#m2" type="button">
                  <i className="fa-solid fa-chart-line me-2"></i>
                  Momento 2 · Seguimiento
                </button>
              </li>
              <li className="nav-item">
                <button className="nav-link" data-bs-toggle="tab" data-bs-target="#m3" type="button">
                  <i className="fa-solid fa-clipboard-check me-2"></i>
                  Momento 3 · Evaluación
                </button>
              </li>
            </ul>
          </div>

          <div className="soft-separator" />

          <div className="tab-content p-3 p-md-4">
            {/* ================= MOMENTO 1 ================= */}
            <div className="tab-pane fade show active" id="m1">
              <div className="row g-3 mt-1">
                <div className="col-12 col-lg-6">
                  <SectionCard
                    icon="fa-solid fa-layer-group"
                    tone="primary"
                    title="Competencias a desarrollar"
                    subtitle="(ver y editar dentro del modal)"
                    action={
                      <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => setShowComp(true)} title="Abrir" aria-label="Abrir">
                        <i className="fa-solid fa-table-list me-1"></i> Abrir
                      </button>
                    }
                  >
                    <div className="mt-2 d-flex align-items-center justify-content-between">
                      <div className="small text-muted">Elementos: {itemsPT?.comp?.length ?? 0}</div>
                      <Pill variant="light">{(itemsPT?.comp?.length ?? 0) > 0 ? "Cargado" : "Por definir"}</Pill>
                    </div>
                  </SectionCard>
                </div>

                <div className="col-12 col-lg-6">
                  <SectionCard
                    icon="fa-solid fa-bullseye"
                    tone="success"
                    title="Resultados de aprendizaje"
                    subtitle="(ver y editar dentro del modal)"
                    action={
                      <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => setShowRes(true)} title="Abrir" aria-label="Abrir">
                        <i className="fa-solid fa-table-list me-1"></i> Abrir
                      </button>
                    }
                  >
                    <div className="mt-2 d-flex align-items-center justify-content-between">
                      <div className="small text-muted">Elementos: {itemsPT?.res?.length ?? 0}</div>
                      <Pill variant="light">{(itemsPT?.res?.length ?? 0) > 0 ? "Cargado" : "Por definir"}</Pill>
                    </div>
                  </SectionCard>
                </div>

                <div className="col-12 col-lg-6">
                  <SectionCard
                    icon="fa-solid fa-list-check"
                    tone="info"
                    title="Actividades a desarrollar"
                    subtitle="(ver y editar dentro del modal)"
                    action={
                      <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => setShowActs(true)} title="Abrir" aria-label="Abrir">
                        <i className="fa-solid fa-table-list me-1"></i> Abrir
                      </button>
                    }
                  >
                    <div className="mt-2 d-flex align-items-center justify-content-between">
                      <div className="small text-muted">Elementos: {itemsPT?.acts?.length ?? 0}</div>
                      <Pill variant="light">{(itemsPT?.acts?.length ?? 0) > 0 ? "Cargado" : "Por definir"}</Pill>
                    </div>
                  </SectionCard>
                </div>

                <div className="col-12 col-lg-6">
                  <SectionCard
                    icon="fa-regular fa-file-lines"
                    tone="warning"
                    title="Evidencias de aprendizaje"
                    subtitle="(ver y editar dentro del modal)"
                    action={
                      <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => setShowEvi(true)} title="Abrir" aria-label="Abrir">
                        <i className="fa-solid fa-table-list me-1"></i> Abrir
                      </button>
                    }
                  >
                    <div className="mt-2 d-flex align-items-center justify-content-between">
                      <div className="small text-muted">Elementos: {itemsPT?.evi?.length ?? 0}</div>
                      <Pill variant="light">{(itemsPT?.evi?.length ?? 0) > 0 ? "Cargado" : "Por definir"}</Pill>
                    </div>
                  </SectionCard>
                </div>

                <div className="col-12">
                  <SectionCard
                    icon="fa-regular fa-comments"
                    tone="secondary"
                    title="Observaciones adicionales"
                    subtitle="(ver y editar dentro del modal)"
                    action={
                      <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => setShowObs(true)} title="Abrir" aria-label="Abrir">
                        <i className="fa-solid fa-table-list me-1"></i> Abrir
                      </button>
                    }
                  >
                    <div className="mt-2 d-flex align-items-center justify-content-between">
                      <div className="small text-muted">Elementos: {itemsPT?.obs?.length ?? 0}</div>
                      <Pill variant="light">{(itemsPT?.obs?.length ?? 0) > 0 ? "Cargado" : "Por definir"}</Pill>
                    </div>
                  </SectionCard>
                </div>
              </div>

              {/* === Firmas M1 === */}
              <div className="mt-3">
                <h6 className="fw-semibold mb-2">
                  <i className="fa-regular fa-pen-to-square me-2 text-primary"></i>Firmas
                </h6>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Ciudad</label>
                    <input name="m1_ciudad" className="form-control" placeholder="Ciudad" defaultValue={form?.m1_ciudad || ""} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Fecha de diligenciamiento</label>
                    <input name="m1_fechaDiligenciamiento" type="date" className="form-control" defaultValue={form?.m1_fechaDiligenciamiento?.slice(0, 10) || ""} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Modalidad de sesión</label>
                    <input name="m1_modalidadSesion" className="form-control" defaultValue={form?.m1_modalidadSesion || ""} />
                  </div>
                </div>
              </div>
            </div>

            {/* ================= MOMENTO 2 ================= */}
            <div className="tab-pane fade" id="m2">
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Fecha del seguimiento</label>
                  <input name="m2_fechaMomentoSeguimiento" type="date" className="form-control" defaultValue={form?.m2_fechaMomentoSeguimiento?.slice(0, 10) || ""} />
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Modalidad</label>
                  <input name="m2_modalidadSeguimiento" className="form-control" defaultValue={form?.m2_modalidadSeguimiento || ""} />
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Enlace de grabación</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white"><i className="fa-solid fa-link"></i></span>
                    <input name="m2_enlaceGrabacion" type="url" className="form-control" defaultValue={form?.m2_enlaceGrabacion || ""} />
                  </div>
                </div>
              </div>

              <div className="row g-3 mt-1">
                <div className="col-12 col-lg-6">
                  <SectionCard
                    icon="fa-solid fa-microscope"
                    tone="primary"
                    title="Factores técnicos"
                    subtitle="(ver y editar dentro del modal)"
                    action={
                      <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => setShowFTm2(true)}>
                        <i className="fa-solid fa-table-list me-1"></i> Abrir
                      </button>
                    }
                  >
                    <div className="mt-2 d-flex align-items-center justify-content-between">
                      <div className="small text-muted">Variables: {ENUMS.factoresTecnicos.length}</div>
                      <Pill variant="light">{Object.keys(m2TecValues).length ? "Cargado" : "Por definir"}</Pill>
                    </div>
                  </SectionCard>
                </div>

                <div className="col-12 col-lg-6">
                  <SectionCard
                    icon="fa-solid fa-users-gear"
                    tone="success"
                    title="Factores actitudinales"
                    subtitle="(ver y editar dentro del modal)"
                    action={
                      <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => setShowFAm2(true)}>
                        <i className="fa-solid fa-table-list me-1"></i> Abrir
                      </button>
                    }
                  >
                    <div className="mt-2 d-flex align-items-center justify-content-between">
                      <div className="small text-muted">Variables: {ENUMS.factoresActitudinales.length}</div>
                      <Pill variant="light">{Object.keys(m2ActValues).length ? "Cargado" : "Por definir"}</Pill>
                    </div>
                  </SectionCard>
                </div>
              </div>

              {/* Observaciones */}
              <div className="row g-3 mt-1">
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Complemento Instructor</label>
                  <textarea name="m2_obs_ComplementInst" className="form-control" rows={3} placeholder="Observaciones del instructor" defaultValue={form?.m2_obs_ComplementInst || ""} />
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Aprendiz</label>
                  <textarea name="m2_obs_Aprendiz" className="form-control" rows={3} placeholder="Observaciones del aprendiz" defaultValue={form?.m2_obs_Aprendiz || ""} />
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Ente coformador</label>
                  <textarea name="m2_obs_EnteCoformador" className="form-control" rows={3} placeholder="Observaciones del ente" defaultValue={form?.m2_obs_EnteCoformador || ""} />
                </div>
              </div>

              {/* Firmas M2 */}
              <div className="mt-3">
                <h6 className="fw-semibold mb-2">
                  <i className="fa-regular fa-pen-to-square me-2 text-primary"></i>Firmas
                </h6>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Ciudad</label>
                    <input name="m2_ciudad" className="form-control" placeholder="Ciudad" defaultValue={form?.m2_ciudad || ""} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Fecha de diligenciamiento</label>
                    <input name="m2_fechaDiligenciamiento" type="date" className="form-control" defaultValue={form?.m2_fechaDiligenciamiento?.slice(0, 10) || ""} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Modalidad de sesión</label>
                    <input name="m2_modalidadSesion" className="form-control" defaultValue={form?.m2_modalidadSesion || ""} />
                  </div>
                </div>
              </div>
            </div>

            {/* ================= MOMENTO 3 ================= */}
            <div className="tab-pane fade" id="m3">
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label small fw-semibold"># Visitas realizadas</label>
                  <input name="m3_numeroVisitasRealizadas" type="number" min={0} className="form-control" placeholder="0" defaultValue={form?.m3_numeroVisitasRealizadas ?? ""} />
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Forma de evaluación</label>
                  <input name="m3_formaEvaluacion" className="form-control" defaultValue={form?.m3_formaEvaluacion || ""} />
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Enlace de grabación</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white"><i className="fa-solid fa-link"></i></span>
                    <input name="m3_enlaceGrabacion" type="url" className="form-control" placeholder="https://…" defaultValue={form?.m3_enlaceGrabacion || ""} />
                  </div>
                </div>
              </div>

              <div className="row g-3 mt-1">
                <div className="col-12 col-lg-6">
                  <SectionCard
                    icon="fa-solid fa-microscope"
                    tone="primary"
                    title="Factores técnicos"
                    subtitle="(ver y editar dentro del modal)"
                    action={
                      <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => setShowFTm3(true)}>
                        <i className="fa-solid fa-table-list me-1"></i> Abrir
                      </button>
                    }
                  >
                    <div className="mt-2 d-flex align-items-center justify-content-between">
                      <div className="small text-muted">Variables: {ENUMS.factoresTecnicos.length}</div>
                      <Pill variant="light">{Object.keys(m3TecValues).length ? "Cargado" : "Por definir"}</Pill>
                    </div>
                  </SectionCard>
                </div>

                <div className="col-12 col-lg-6">
                  <SectionCard
                    icon="fa-solid fa-users-gear"
                    tone="success"
                    title="Factores actitudinales"
                    subtitle="(ver y editar dentro del modal)"
                    action={
                      <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => setShowFAm3(true)}>
                        <i className="fa-solid fa-table-list me-1"></i> Abrir
                      </button>
                    }
                  >
                    <div className="mt-2 d-flex align-items-center justify-content-between">
                      <div className="small text-muted">Variables: {ENUMS.factoresActitudinales.length}</div>
                      <Pill variant="light">{Object.keys(m3ActValues).length ? "Cargado" : "Por definir"}</Pill>
                    </div>
                  </SectionCard>
                </div>
              </div>

              {/* Retroalimentaciones (lectura) */}
              <div className="row g-3 mt-1">
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Ente — Proceso de formación</label>
                  <textarea name="m3_retroEnte_proceso" className="form-control" rows={3} placeholder="Proceso de formación" defaultValue={form?.m3_retroEnte_proceso || ""} readOnly />
                </div>

                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Instructor — Proceso de formación</label>
                  <textarea name="m3_retroInstructor_proceso" className="form-control" rows={3} placeholder="Proceso de formación" defaultValue={form?.m3_retroInstructor_proceso || ""} readOnly />
                </div>

                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Aprendiz — Proceso de formación</label>
                  <textarea name="m3_retroAprendiz_proceso" className="form-control" rows={3} placeholder="Proceso de formación" defaultValue={form?.m3_retroAprendiz_proceso || ""} readOnly />
                </div>

                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Ente — Desempeño de competencias</label>
                  <textarea name="m3_retroEnte_desempeno" className="form-control" rows={3} placeholder="Desempeño de competencias" defaultValue={form?.m3_retroEnte_desempeno || ""} readOnly />
                </div>

                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Instructor — Desempeño de competencias</label>
                  <textarea name="m3_retroInstructor_desempeno" className="form-control" rows={3} placeholder="Desempeño de competencias" defaultValue={form?.m3_retroInstructor_desempeno || ""} readOnly />
                </div>

                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Aprendiz — Desempeño de competencias</label>
                  <textarea name="m3_retroAprendiz_desempeno" className="form-control" rows={3} placeholder="Desempeño de competencias" defaultValue={form?.m3_retroAprendiz_desempeno || ""} readOnly />
                </div>
              </div>

              {/* Juicio + firmas (lectura) */}
              <div className="row g-3 mt-1">
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Juicio de evaluación</label>
                  <input name="m3_juicioEvaluacion" className="form-control" defaultValue={form?.m3_juicioEvaluacion || ""} readOnly />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Modalidad de sesión</label>
                  <input name="m3_modalidadSesion" className="form-control" defaultValue={form?.m3_modalidadSesion || ""} readOnly />
                </div>
              </div>

              <div className="mt-3">
                <h6 className="fw-semibold mb-2">
                  <i className="fa-regular fa-pen-to-square me-2 text-primary"></i>Firmas
                </h6>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Ciudad</label>
                    <input name="m3_ciudad" className="form-control" placeholder="Ciudad" defaultValue={form?.m3_ciudad || ""} readOnly />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Fecha de diligenciamiento</label>
                    <input name="m3_fechaDiligenciamiento" type="date" className="form-control" defaultValue={form?.m3_fechaDiligenciamiento?.slice(0, 10) || ""} readOnly />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Modalidad de sesión</label>
                    <input name="m3_modalidadSesion" className="form-control" defaultValue={form?.m3_modalidadSesion || ""} readOnly />
                  </div>
                </div>
              </div>
            </div>
            {/* FIN MOMENTO 3 */}
          </div>
        </div>
      </div>

      {/* Footer de acciones */}
      <div className="d-flex justify-content-end gap-2 mt-3">
        <button className="btn btn-ghost" type="button" disabled={saving}>
          <i className="fa-regular fa-circle-xmark me-1"></i> Cancelar
        </button>
        {((!formato && puedeCrear) || (formato && puedeActualizar)) && (
          <button className="btn btn-primary" type="submit" disabled={saving || loading}>
            <i className="fa-solid fa-floppy-disk me-1"></i> {formato ? "Actualizar" : "Guardar"}
          </button>
        )}
      </div>

      {/* ====== MODALES (M1) ====== */}
      <ArrayEditorModal
        show={showComp}
        onClose={() => setShowComp(false)}
        title="Competencias a Desarrollar"
        placeholder="Competencia…"
        items={itemsPT.comp}
        onAdd={async (v) => { if (!formato?._id) await saveSeguimiento(); await pushToArray(formato?._id, pathPT.comp, [v]); await loadMeta(); }}
        onRemove={async (v) => { if (!formato?._id) return; await pullFromArray(formato._id, pathPT.comp, [v]); await loadMeta(); }}
        onEdit={async (oldVal, newVal) => { if (!formato?._id) await saveSeguimiento(); await replaceInArray(formato._id, pathPT.comp, oldVal, newVal); await loadMeta(); }}
      />
      <ArrayEditorModal
        show={showRes}
        onClose={() => setShowRes(false)}
        title="Resultados de Aprendizaje"
        placeholder="Resultado…"
        items={itemsPT.res}
        onAdd={async (v) => { if (!formato?._id) await saveSeguimiento(); await pushToArray(formato?._id, pathPT.res, [v]); await loadMeta(); }}
        onRemove={async (v) => { if (!formato?._id) return; await pullFromArray(formato._id, pathPT.res, [v]); await loadMeta(); }}
        onEdit={async (oldVal, newVal) => { if (!formato?._id) await saveSeguimiento(); await replaceInArray(formato._id, pathPT.res, oldVal, newVal); await loadMeta(); }}
      />
      <ArrayEditorModal
        show={showActs}
        onClose={() => setShowActs(false)}
        title="Actividades a Desarrollar"
        placeholder="Actividad…"
        items={itemsPT.acts}
        onAdd={async (v) => { if (!formato?._id) await saveSeguimiento(); await pushToArray(formato._id, pathPT.acts, [v]); await loadMeta(); }}
        onRemove={async (v) => { if (!formato?._id) return; await pullFromArray(formato._id, pathPT.acts, [v]); await loadMeta(); }}
        onEdit={async (oldVal, newVal) => { if (!formato?._id) await saveSeguimiento(); await replaceInArray(formato._id, pathPT.acts, oldVal, newVal); await loadMeta(); }}
      />
      <ArrayEditorModal
        show={showEvi}
        onClose={() => setShowEvi(false)}
        title="Evidencias de Aprendizaje"
        placeholder="Evidencia…"
        items={itemsPT.evi}
        onAdd={async (v) => { if (!formato?._id) await saveSeguimiento(); await pushToArray(formato._id, pathPT.evi, [v]); await loadMeta(); }}
        onRemove={async (v) => { if (!formato?._id) return; await pullFromArray(formato._id, pathPT.evi, [v]); await loadMeta(); }}
        onEdit={async (oldVal, newVal) => { if (!formato?._id) await saveSeguimiento(); await replaceInArray(formato._id, pathPT.evi, oldVal, newVal); await loadMeta(); }}
      />
      <ArrayEditorModal
        show={showObs}
        onClose={() => setShowObs(false)}
        title="Observaciones Adicionales"
        placeholder="Observación…"
        items={itemsPT.obs}
        onAdd={async (v) => { if (!formato?._id) await saveSeguimiento(); await pushToArray(formato._id, pathPT.obs, [v]); await loadMeta(); }}
        onRemove={async (v) => { if (!formato?._id) return; await pullFromArray(formato._id, pathPT.obs, [v]); await loadMeta(); }}
        onEdit={async (oldVal, newVal) => { if (!formato?._id) await saveSeguimiento(); await replaceInArray(formato._id, pathPT.obs, oldVal, newVal); await loadMeta(); }}
      />

      {/* M2/M3 factores (onSet listo para conectar si deseas persistencia inmediata) */}
      <FactoresTableModal
        show={showFTm2}
        onClose={() => setShowFTm2(false)}
        title="Momento 2 — Factores Técnicos"
        variables={ENUMS.factoresTecnicos}
        valoracionEnum={ENUMS.valoracion}
        values={m2TecValues}
        onSet={(variable, field, value) => {
          // ejemplo: updateSeguimiento({ m2_factoresTecnicos: [...] })
        }}
      />
      <FactoresTableModal
        show={showFAm2}
        onClose={() => setShowFAm2(false)}
        title="Momento 2 — Factores Actitudinales"
        variables={ENUMS.factoresActitudinales}
        valoracionEnum={ENUMS.valoracion}
        values={m2ActValues}
        onSet={(variable, field, value) => {
          // ejemplo: updateSeguimiento({ m2_factoresActitudinales: [...] })
        }}
      />
      <FactoresTableModal
        show={showFTm3}
        onClose={() => setShowFTm3(false)}
        title="Momento 3 — Factores Técnicos"
        variables={ENUMS.factoresTecnicos}
        valoracionEnum={ENUMS.valoracion}
        values={m3TecValues}
        onSet={(variable, field, value) => {
          // ejemplo: updateSeguimiento({ m3_factoresTecnicos: [...] })
        }}
      />
      <FactoresTableModal
        show={showFAm3}
        onClose={() => setShowFAm3(false)}
        title="Momento 3 — Factores Actitudinales"
        variables={ENUMS.factoresActitudinales}
        valoracionEnum={ENUMS.valoracion}
        values={m3ActValues}
        onSet={(variable, field, value) => {
          // ejemplo: updateSeguimiento({ m3_factoresActitudinales: [...] })
        }}
      />
    </form>
  );
};

export default RegistrarSegPracticaAprendiz;
